/**
 * Design-check results (CLAUDE.md 5.1, 14.2, 14.5).
 *
 * The defect this fixes is subtle and dangerous: while a preview was in flight, the
 * previous results stayed at full opacity with only a small "Loading" caption under
 * the *form*. So an engineer who changed a span and glanced right read numbers that
 * no longer corresponded to the inputs on screen. Reading a stale number as a current
 * one is the worst failure this UI can have, so staleness is now unmissable --
 * dimmed, labelled, and announced.
 *
 * Status is never signalled by colour alone: every row carries an icon and a text
 * label as well, for colour-vision deficiency and for the greyscale printing that
 * calculation sheets routinely get (14.2).
 */
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Loader2 } from 'lucide-react'

import type { Check, CalcWarning } from '@/api/types'
import { cn } from '@/lib/cn'
import { formatQuantity, formatUtilisation } from '@/lib/format'
import { StatusBadge, type Status } from '@/components/ui/StatusBadge'
import { UtilisationBar } from '@/components/ui/UtilisationBar'
import { ClauseRef } from '@/components/ui/tooltip'

export function ResultsPanel({
  checks,
  warnings,
  governingRatio,
  status,
  stale,
  pending,
  error,
  onRetry,
}: {
  checks: Check[]
  warnings: CalcWarning[]
  governingRatio: number | null
  status: Status | null
  /** The displayed numbers no longer match the inputs on screen. */
  stale?: boolean
  pending?: boolean
  error?: string | null
  onRetry?: () => void
}) {
  const { t } = useTranslation(['common', 'report'])

  return (
    <div className="space-y-5">
      {error && (
        <div
          role="alert"
          className="flex items-start justify-between gap-3 rounded-md bg-[var(--status-fail-bg)] p-3 text-sm text-[var(--status-fail)]"
        >
          <span>{error}</span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="shrink-0 font-medium underline underline-offset-2"
            >
              {t('action.retry')}
            </button>
          )}
        </div>
      )}

      {warnings.map((warning) => (
        <p
          key={warning.code}
          className={cn(
            'flex items-start gap-2 rounded-md p-3 text-sm',
            warning.severity === 'critical'
              ? 'bg-[var(--status-fail-bg)] text-[var(--status-fail)]'
              : 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]',
          )}
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
          {t(warning.message_key, { ns: 'report' })}
        </p>
      ))}

      {/*
        The results themselves. `aria-live="polite"` means a screen-reader user is
        told the numbers changed after they typed -- previously the update was
        completely silent (14.5). `aria-busy` marks the region as updating rather
        than broken.
      */}
      <div aria-live="polite" aria-busy={pending || undefined} className="relative space-y-5">
        {(stale || pending) && (
          <div className="flex items-center gap-2 rounded-md bg-[var(--status-warn-bg)] px-3 py-2 text-xs text-[var(--status-warn)]">
            {pending ? (
              <Loader2 size={13} className="animate-spin" aria-hidden />
            ) : (
              <AlertTriangle size={13} aria-hidden />
            )}
            {pending ? t('results.recalculating') : t('results.stale')}
          </div>
        )}

        <div
          className={cn(
            'space-y-5 transition-opacity',
            // Dimmed *and* labelled: opacity alone would be a colour-only signal.
            (stale || pending) && 'opacity-50',
          )}
        >
          {status && (
            <div className="flex flex-wrap items-center gap-3">
              <StatusBadge status={status} />
              {governingRatio !== null && (
                <span className="tabular text-sm text-text-muted">
                  {t('common.utilisation')} {formatUtilisation(governingRatio)}
                </span>
              )}
            </div>
          )}

          {checks.length > 0 && (
            <>
              <section className="space-y-2" aria-label={t('common.utilisation')}>
                {checks.map((check) => (
                  <UtilisationBar
                    key={check.key}
                    ratio={check.ratio}
                    label={t(check.name_key, { ns: 'report' })}
                  />
                ))}
              </section>

              <div className="scroll-x">
                <table className="w-full min-w-[36rem] border-collapse text-sm">
                  <caption className="sr-only">{t('common.check')}</caption>
                  <thead>
                    <tr className="border-b border-border text-text-muted">
                      <th scope="col" className="py-2 text-start font-medium">
                        {t('common.check')}
                      </th>
                      <th scope="col" className="py-2 text-start font-medium">
                        {t('common.clause')}
                      </th>
                      <th scope="col" className="py-2 text-end font-medium">
                        {t('common.demand')}
                      </th>
                      <th scope="col" className="py-2 text-end font-medium">
                        {t('common.capacity')}
                      </th>
                      <th scope="col" className="py-2 text-end font-medium">
                        {t('common.utilisation')}
                      </th>
                      {/* The governing combination answers "why does this check
                          govern?", which `CalcResult.envelope` already carries (5.1). */}
                      <th scope="col" className="py-2 text-start font-medium">
                        {t('common.governing')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {checks.map((check) => (
                      <tr key={check.key} className="border-b border-border">
                        <th scope="row" className="py-2 text-start font-normal">
                          {t(check.name_key, { ns: 'report' })}
                        </th>
                        <td className="py-2">
                          {check.clause ? <ClauseRef clause={check.clause} /> : '—'}
                        </td>
                        <td className="tabular py-2 text-end">
                          {formatQuantity(check.demand, 'moment', check.unit)}
                        </td>
                        <td className="tabular py-2 text-end">
                          {formatQuantity(check.capacity, 'moment', check.unit)}
                        </td>
                        <td
                          className={cn(
                            'tabular py-2 text-end font-medium',
                            check.ratio !== null && check.ratio > 1 && 'text-[var(--status-fail)]',
                          )}
                        >
                          {formatUtilisation(check.ratio)}
                        </td>
                        <td className="py-2 text-text-muted">{check.governing_combo ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
